import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { endpoints, setUnauthorizedHandler } from '../api/client.js';

const AuthContext = createContext(null);

/**
 * status:
 *   'checking'  initial /api/me probe in flight — render a loading state, never
 *               the login screen, so a signed-in reload doesn't flash.
 *   'authed'    session confirmed
 *   'anon'      no session (or a 401 arrived from any endpoint)
 */
export function AuthProvider({ children }) {
  const [status, setStatus] = useState('checking');
  const [user, setUser] = useState(null);
  const [signedOutReason, setSignedOutReason] = useState(null);

  // Central 401 handling: one place, wired into the one fetch helper.
  useEffect(() => {
    setUnauthorizedHandler((error) => {
      setUser(null);
      setSignedOutReason(error?.message || 'Your session has expired.');
      setStatus('anon');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    endpoints
      .me(controller.signal)
      .then((payload) => {
        if (!alive) return;
        setUser(payload.user || null);
        setStatus('authed');
      })
      .catch((error) => {
        if (!alive || error?.name === 'AbortError') return;
        setStatus('anon');
        // A 401 just means "not signed in" and needs no explanation. Anything
        // else (the API is down, the proxy is misconfigured) does.
        if (error?.status !== 401) {
          setSignedOutReason(
            error?.message || 'The control panel API did not answer. Sign-in may not work yet.',
          );
        }
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  const signIn = useCallback(async (username, password) => {
    const payload = await endpoints.login(username, password);
    setUser(payload.user || null);
    setSignedOutReason(null);
    setStatus('authed');
    return payload.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await endpoints.logout();
    } finally {
      setUser(null);
      setSignedOutReason(null);
      setStatus('anon');
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const payload = await endpoints.changePassword(currentPassword, newPassword);
    setUser(payload.user || null);
    return payload.user;
  }, []);

  const value = useMemo(
    () => ({ status, user, signedOutReason, signIn, signOut, changePassword }),
    [status, user, signedOutReason, signIn, signOut, changePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
